import { Link } from "react-router-dom";
import { LOGO_URL, NAV_LINKS, TAGLINE, BUSINESS_NAME, WHATSAPP_COMMUNITY_URL, BOOK_CONSULTATION_URL, COMPASS_ICON_URL } from "@/lib/brand";
import WhatsAppIcon from "@/components/WhatsAppIcon";

export default function Footer() {
  return (
    <footer className="mt-24 border-t hairline bg-paper-raised" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-14 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <img src={LOGO_URL} alt="Reliserve Wealth" className="h-[72px] w-auto mb-5" />
            <p className="font-display text-[19px] leading-snug text-ink max-w-md">
              {TAGLINE}
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow mb-4">Explore</div>
            <ul className="space-y-2.5">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-[14px] text-ink-soft hover:text-brass-deep transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/grievance-redressal" className="text-[14px] text-ink-soft hover:text-brass-deep transition-colors" data-testid="footer-link-grievance">
                  Grievance Redressal
                </Link>
              </li>
              <li>
                <Link to="/compass" className="text-[14px] text-ink-soft hover:text-brass-deep transition-colors inline-flex items-center gap-1.5" data-testid="footer-link-compass">
                  <img src={COMPASS_ICON_URL} alt="" className="w-3.5 h-3.5" />
                  Reliserve Wealth Compass
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="eyebrow mb-4">Get in touch</div>
            <p className="text-[14px] text-ink-soft leading-relaxed">
              Phone: <span className="num">+91 70289 77777</span><br />
              Email:{" "}
              <a href="mailto:contact@reliservewealth.com" className="num link-brass">
                contact@reliservewealth.com
              </a>
            </p>
            <p className="text-[14px] text-ink-soft leading-relaxed mt-3">
              Reliserve Wealth<br />
              #304, 3rd Floor, Laxmi Towers,<br />
              Swami Vivekananda Road,<br />
              Opp Junta House,<br />
              Panaji 403 001, Goa, INDIA
            </p>
            <a
              href={BOOK_CONSULTATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline mt-5 text-[13px]"
              data-testid="footer-cta-schedule"
            >
              Schedule a Consultation
            </a>
            <a
              href={WHATSAPP_COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-3 text-[13px] text-brass-deep hover:text-brass transition-colors group"
              data-testid="footer-whatsapp-community"
            >
              <WhatsAppIcon size={14} />
              <span className="border-b border-brass/30 group-hover:border-brass transition-colors">
                Join our WhatsApp community
              </span>
            </a>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t hairline text-[12px] leading-relaxed text-ink-soft">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="footer-disclaimer-blocks">
            <DisclaimerBlock title="Regulatory Information">
              <p>
                <span className="text-ink font-medium">{BUSINESS_NAME}</span> · Mutual Fund
                Distributor · ARN <span className="num">369518</span> · Licensed Insurance Agent.
              </p>
              <p className="mt-2">
                {BUSINESS_NAME} operates under the regulatory framework laid down by SEBI and IRDAI
                for all the services it offers. We adhere to the Code of Conduct for Mutual Fund
                Distributors prescribed by AMFI (Association of Mutual Funds in India).
              </p>
            </DisclaimerBlock>

            <DisclaimerBlock title="Mutual Fund Disclaimer">
              <p>
                Mutual fund investments are subject to market risks. Please read the scheme
                information document and key information memorandum carefully before investing. Past
                performance is not indicative of future returns. {BUSINESS_NAME} is a mutual fund
                distributor registered with AMFI. {BUSINESS_NAME} does not guarantee or assure any
                returns on investments.
              </p>
            </DisclaimerBlock>

            <DisclaimerBlock title="Insurance Disclaimer">
              <p>
                Insurance is the subject matter of solicitation. Rohan Ghotage ({BUSINESS_NAME}) is
                a licensed insurance agent registered with IRDAI. We do not underwrite an insurance
                policy nor can we influence any insurance decision. Please read the policy terms
                and conditions carefully before concluding the sale. The purchase of insurance is
                voluntary.
              </p>
            </DisclaimerBlock>
          </div>

          <div className="mt-10 pt-6 border-t hairline space-y-4">
            <p>
              For more information, visit our{" "}
              <Link to="/regulatory" className="link-brass" data-testid="footer-link-regulatory">
                Regulatory Page
              </Link>
              .
            </p>
            <p>
              <span className="text-ink font-medium">Grievance Redressal:</span> If you have a
              concern about our service, please write to{" "}
              <a href="mailto:grievance@reliservewealth.com" className="link-brass">
                grievance@reliservewealth.com
              </a>{" "}
              or use our{" "}
              <Link to="/grievance-redressal" className="link-brass">
                Grievance Redressal Form
              </Link>{" "}
              for a tracked response. Unresolved complaints relating to mutual funds may be
              escalated to AMFI (amfiindia.com), and those relating to insurance to IRDAI
              (irdai.gov.in) or the Insurance Ombudsman.
            </p>
            <p>
              <span className="text-ink font-medium">{BUSINESS_NAME}</span> is the Trade Name under
              which <span className="text-ink font-medium">Rohan Ghotage</span> operates.
            </p>
            <p className="pt-4 border-t hairline text-ink-soft/80">
              © {new Date().getFullYear()} {BUSINESS_NAME}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

function DisclaimerBlock({ title, children }) {
  return (
    <div data-testid={`footer-disclaimer-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="eyebrow mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
